// Email (Phase 5): aggregate ActiveCampaign rollup. KPI tiles (list size,
// growth, open/click rates over recent sends), the campaign table, and
// the automations list. Data from /api/data/email-rollup (cached in lib)
// plus the audience summary already in MarketingContext.

import { useEffect, useState } from 'react';
import { useMarketing } from '../MarketingContext';
import { apiFetch } from '../../../utils/apiFetch';
import KpiTile from '../dashboard/KpiTile';
import { Card, Eyebrow, Pill } from '../ui';

export default function EmailView() {
  const { ac } = useMarketing();
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  async function load(force = false) {
    setErr(null);
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const res = await apiFetch(force ? '/api/data/email-rollup/refresh' : '/api/data/email-rollup', {
        method: force ? 'POST' : 'GET',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRollup(json);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { load(false); }, []);

  const acData = ac.data && ac.data.configured !== false ? ac.data : null;
  const agg = rollup?.configured !== false ? rollup?.aggregate : null;

  if (!loading && rollup?.configured === false) {
    return (
      <Card className="p-5 border-dashed max-w-[720px]">
        <Eyebrow className="mb-2">Email performance</Eyebrow>
        <div className="text-[12px] text-ink-2">
          ActiveCampaign env vars not set. Add <code className="font-mono bg-bone/60 px-1 rounded">AC_API_URL</code> and{' '}
          <code className="font-mono bg-bone/60 px-1 rounded">AC_API_KEY</code> in Vercel to populate.
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
        <KpiTile
          lash="teal"
          label="List size"
          loading={ac.loading}
          value={acData ? acData.total.toLocaleString() : '—'}
          foot="active subscribers"
        />
        <KpiTile
          lash="blue"
          label="30-day growth"
          loading={ac.loading}
          value={acData ? `+${acData.monthly.new.toLocaleString()}` : '—'}
          delta={acData?.monthly?.delta != null ? `${acData.monthly.delta >= 0 ? '+' : ''}${acData.monthly.delta}` : null}
          deltaDir={acData?.monthly?.delta > 0 ? 'up' : acData?.monthly?.delta < 0 ? 'down' : 'flat'}
          foot="vs prior 30d"
        />
        <KpiTile
          lash="sky"
          label="Avg open rate"
          loading={loading}
          value={agg ? `${(agg.avgOpenRate * 100).toFixed(1)}%` : '—'}
          foot={agg ? `last ${agg.campaignCount} sends` : ''}
        />
        <KpiTile
          lash="navy"
          label="Avg click rate"
          loading={loading}
          value={agg ? `${(agg.avgClickRate * 100).toFixed(1)}%` : '—'}
          foot={agg ? `${agg.unsubscribes} unsubs total` : ''}
        />
      </div>

      <Card lash="teal" className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Eyebrow>Campaigns · most recent sends</Eyebrow>
          <button
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className={`text-[10px] font-bold px-2 py-0.5 rounded-[5px] ${
              refreshing || loading ? 'bg-line-2 text-ink-3' : 'bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20'
            }`}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loading && <div className="text-[12px] text-ink-3">Loading from ActiveCampaign…</div>}
        {err && <div className="text-[12px] text-rose">Failed: {err}</div>}

        {!loading && rollup?.campaigns?.length === 0 && (
          <div className="text-[12.5px] text-ink-2">No sent campaigns yet.</div>
        )}

        {rollup?.campaigns?.length > 0 && (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Campaign', 'Sent', 'Sends', 'Opens', 'Open %', 'Clicks', 'Click %', 'Unsub'].map((h, i) => (
                  <th key={h} className={`text-[10.5px] tracking-[0.06em] uppercase text-ink-3 font-black px-2.5 py-2 border-b border-line ${i === 0 ? 'text-left' : 'text-right'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rollup.campaigns.map(c => (
                <tr key={c.id} className="hover:bg-[#faf9f6]">
                  <td className="px-2.5 py-2 border-b border-line-2 text-[13px] font-semibold text-navy">{c.name}</td>
                  <Td>{c.sentAt ? String(c.sentAt).slice(0, 10) : '—'}</Td>
                  <Td>{c.sends.toLocaleString()}</Td>
                  <Td>{c.opens.toLocaleString()}</Td>
                  <Td strong>{(c.openRate * 100).toFixed(1)}%</Td>
                  <Td>{c.clicks.toLocaleString()}</Td>
                  <Td strong>{(c.clickRate * 100).toFixed(1)}%</Td>
                  <Td>{c.unsubscribes}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {rollup?.automations?.length > 0 && (
        <Card lash="sky" className="p-4">
          <Eyebrow className="mb-3">Automations</Eyebrow>
          <div className="flex flex-col gap-1.5">
            {rollup.automations.map(a => (
              <div key={a.id} className="flex items-center justify-between px-2.5 py-2 rounded-[7px] bg-[#faf9f6]">
                <span className="text-[13px] font-semibold text-ink">{a.name}</span>
                <span className="flex items-center gap-2.5">
                  <span className="text-[12px] text-ink-2 tabular-nums">
                    {a.entered != null ? `${a.entered.toLocaleString()} entered` : ''}
                  </span>
                  <Pill tone={a.status === 'active' ? 'good' : 'neutral'} className="text-[10px]">{a.status}</Pill>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Td({ children, strong }) {
  return (
    <td className={`px-2.5 py-2 border-b border-line-2 text-[13px] text-right tabular-nums ${strong ? 'font-bold text-navy' : 'text-ink-2'}`}>
      {children}
    </td>
  );
}
